/**
 * Whether a CSS media query matches, kept current as the window changes.
 *
 * Read synchronously on the first render so a layout chosen by width is
 * right from the start rather than corrected a frame later.
 *
 * Who uses it: the plant and bed cards and the plant filter sheet (phone or
 * wider), and TooltipContext.
 *
 * @module useMediaQuery
 */

import { useEffect, useState } from 'react';

/**
 * Hook that tracks state of a CSS media query
 *
 * @param query - CSS media query string to evaluate
 * @returns boolean indicating if the media query matches
 *
 * @example
 * const isMobile = useMediaQuery('(max-width: 768px)');
 */
export function useMediaQuery(query: string): boolean {
  // Read synchronously so the first render is already right, rather than
  // rendering the narrow layout and correcting it a frame later.
  /** Whether the query matches now; false where there is no window. */
  const getMatches = (): boolean => (
    typeof window === 'undefined' ? false : window.matchMedia(query).matches
  );

  const [matches, setMatches] = useState(getMatches);

  useEffect(() => {
    const media = window.matchMedia(query);
    setMatches(media.matches);

    /** Follows the query as it starts or stops matching. */
    const listener = (event: MediaQueryListEvent): void => setMatches(event.matches);
    media.addEventListener('change', listener);
    return () => media.removeEventListener('change', listener);
  }, [query]);

  return matches;
}
